import { pdf } from '@react-pdf/renderer';
import { ModalContext } from 'app/app';
import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import { ViewContext } from 'components/lib';
import { Form, Formik } from 'formik';
import DateUtils from 'helpers/dateUtils';
import { Fragment, useContext, useState } from 'react';
import * as Yup from 'yup';
import Loader from './images/loader.gif';
import QuotePDF from './pdf';
import styles from './styles.module.scss';
import QuoteUtils from 'helpers/quoteUtils';

export function QuotePDFModal(props) {
  const view = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);

  const date_helper = new DateUtils();
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumSignificantDigits: 7,
  });

  const validationSchema = Yup.object().shape({
    submission_number: Yup.string().required(
      'This field is required!'
    ),
  });

  const quoteUtils = new QuoteUtils();

  async function runAPI(submission_number) {
    return await axios
      .get('/api/quote/info/' + submission_number)
      .then((res) => {
        if (res.data.data.error)
          throw Error('Submission number not found');
        if (!res.data.data.premiumFinanceDetails.downPayment)
          throw Error('Finance datails not avaliable yet');

        //======================== Add all coverages to be mapped and generate total value ========================

        const { total, coverageList } = quoteUtils.calculatePremium(
          res.data.data.coverages
        );

        setLoading(false);

        res.data.data.premiumFinanceDetails.downPayment =
          Number(res.data.data.premiumFinanceDetails.downPayment) +
          res.data.data.vehicles.length * 400;

        return {
          success: true,
          data: {
            dot_number: '00000',
            effective_date:
              res.data.data.coverages[
                Object.keys(res.data.data.coverages)[0]
              ].policy_effective_date,
            company_name: res.data.data.insuredInformation.legal_name,
            coverages: coverageList,
            total,
            financeDetails: res.data.data.premiumFinanceDetails,
            surplusLine: res.data.data.premiumFinanceDetails
              .downPayment
              ? Number(
                  res.data.data.premiumFinanceDetails.downPayment
                ) +
                Number(
                  res.data.data.premiumFinanceDetails.paymentAmount
                ) *
                  res.data.data.premiumFinanceDetails
                    .numberOfPayments -
                total
              : null,
            downPayment: res.data.data.premiumFinanceDetails
              .downPayment
              ? parseInt(
                  res.data.data.premiumFinanceDetails.downPayment
                ) +
                (res.data.data.coverages.al ? 300 : 0) +
                (res.data.data.coverages.apd ? 200 : 0) +
                (res.data.data.coverages.mtc ? 100 : 0) +
                (res.data.data.coverages.tgl ? 100 : 0)
              : 0,
            fees:
              (res.data.data.coverages.al ? 300 : 0) +
              (res.data.data.coverages.apd ? 200 : 0) +
              (res.data.data.coverages.mtc ? 100 : 0) +
              (res.data.data.coverages.tgl ? 100 : 0),
          },
        };
      })
      .catch((err) => {
        setLoading(false);

        return { error: true, message: err.message };
      });
  }

  return (
    <Fragment>
      {modal.name === 'quotepdf_modal' && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>Generate Quote PDF</div>
              <div className={styles.line} />
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  submission_number: '',
                }}
                onSubmit={async (values, { setErrors }) => {
                  setLoading(true);

                  try {
                    const response = await runAPI(
                      values.submission_number
                    );

                    if (response && response.error)
                      setErrors({
                        submission_number: response.message,
                      });

                    setLoading(true);

                    const pdfBlob = await pdf(
                      <QuotePDF
                        data={{
                          dot_number: response.data.dotNumber,
                          submission_number: values.submission_number,
                          effective_date:
                            date_helper.formatIsoToCommomDate(
                              response.data.effective_date
                            ),
                          company_name: response.data.legalName,
                          total_policy_cost: formatter.format(
                            response.data.total +
                              response.data.surplusLine +
                              response.data.fees
                          ),
                          policy_premium: formatter.format(
                            response.data.total
                          ),
                          surplus_line: formatter.format(
                            response.data.surplusLine
                          ),
                          fees: formatter.format(response.data.fees),
                          policy_cost: formatter.format(
                            response.data.total +
                              response.data.surplusLine +
                              response.data.fees
                          ),
                          coverages: response.data.coverages,
                          finance_details: {
                            down_payment: formatter.format(
                              response.data.financeDetails.downPayment
                            ),
                            number_of_payments:
                              response.data.financeDetails
                                .numberOfPayments,
                            payment_amount: formatter.format(
                              response.data.financeDetails
                                .paymentAmount
                            ),
                            effectiveAPR: `${response.data.financeDetails.effectiveAPR}%`,
                            premiumFinanceProvider:
                              response.data.financeDetails.premiumFinanceProvider.toUpperCase(),
                          },
                        }}
                      />
                    ).toBlob();

                    const formData = new FormData();
                    formData.append('file', pdfBlob, `quote.pdf`);

                    await axios
                      .post('/api/quote/file', formData)
                      .then(async (pdf_response) => {
                        const link = pdf_response.data.link;

                        view.notification.show(
                          'PDF Downloaded.',
                          'success',
                          true,
                          'toast',
                          'check-circle'
                        );

                        window.open(link);
                        setModal({});
                      })
                      .catch((err) => {
                        setModal({});

                        view.notification.show(
                          'Error on upload certificate to server',
                          'error',
                          true,
                          'toast',
                          'x-circle'
                        );
                      });
                  } catch (error) {
                    console.log(error);
                  }

                  setLoading(false);
                }}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form
                      className={styles.form}
                      onSubmit={handleSubmit}
                    >
                      <div
                        className={styles.fieldRow}
                        style={{ paddingBottom: 0 }}
                      >
                        <FieldInput
                          id="submission_number"
                          type="text"
                          label="Submission Number"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.submission_number}
                          touched={touched.submission_number}
                        />
                      </div>
                    </Form>
                    <div
                      className={styles.footer}
                      style={{ justifyContent: 'flex-end' }}
                    >
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={() => setModal('')}
                        >
                          Cancel
                        </button>
                        <button
                          className={styles.save}
                          onClick={submitForm}
                        >
                          {loading ? (
                            <img
                              src={Loader}
                              alt="Loader"
                              className={styles.loader}
                            />
                          ) : (
                            'Create'
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
